import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FormField } from '@/components/shared/form-field';
import { PageHeader } from '@/components/shared/page-header';
import { ReadOnlyNotice } from '@/components/shared/read-only-notice';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/toast';
import { useIsPlatformAdmin } from '@/hooks/use-is-platform-admin';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';

interface PlatformAppVersion {
  ios: string;
  android: string;
}

const VERSION_PATTERN = /^\d+\.\d+\.\d+$/;

export const AppVersionPage = () => {
  const { t } = useTranslation('admin');
  const isAdmin = useIsPlatformAdmin();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();

  const [ios, setIos] = useState('1.0.0');
  const [android, setAndroid] = useState('1.0.0');
  const [iosError, setIosError] = useState<string | undefined>();
  const [androidError, setAndroidError] = useState<string | undefined>();

  const version = useQuery({
    queryKey: ['platform-app-version'],
    queryFn: () => api.get<PlatformAppVersion>('/platform/app-version'),
  });

  useEffect(() => {
    if (!version.data) return;
    setIos(version.data.ios);
    setAndroid(version.data.android);
  }, [version.data]);

  const save = useMutation({
    mutationFn: () => api.patch('/platform/app-version', { ios: ios.trim(), android: android.trim() }),
    onSuccess: async () => {
      toast.success(t('appVersion.saved'));
      await queryClient.invalidateQueries({ queryKey: ['platform-app-version'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const onSave = () => {
    const nextIos = ios.trim();
    const nextAndroid = android.trim();
    const iosInvalid = !VERSION_PATTERN.test(nextIos);
    const androidInvalid = !VERSION_PATTERN.test(nextAndroid);
    setIosError(iosInvalid ? t('appVersion.invalid') : undefined);
    setAndroidError(androidInvalid ? t('appVersion.invalid') : undefined);
    if (iosInvalid || androidInvalid) return;
    save.mutate();
  };

  return (
    <div>
      <PageHeader title={t('appVersion.title')} description={t('appVersion.description')} />
      <ReadOnlyNotice />

      <Card>
        <CardHeader>
          <CardTitle>{t('appVersion.cardTitle')}</CardTitle>
          <CardDescription>{t('appVersion.cardDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField label={t('appVersion.ios')} error={iosError} required>
              {(field) => (
                <Input
                  {...field}
                  value={ios}
                  onChange={(event) => {
                    setIos(event.target.value);
                    if (iosError) setIosError(undefined);
                  }}
                  placeholder={t('appVersion.placeholder')}
                  autoComplete="off"
                  spellCheck={false}
                />
              )}
            </FormField>
            <FormField label={t('appVersion.android')} error={androidError} required>
              {(field) => (
                <Input
                  {...field}
                  value={android}
                  onChange={(event) => {
                    setAndroid(event.target.value);
                    if (androidError) setAndroidError(undefined);
                  }}
                  placeholder={t('appVersion.placeholder')}
                  autoComplete="off"
                  spellCheck={false}
                />
              )}
            </FormField>
          </div>

          {isAdmin ? (
            <div className="flex justify-end">
              <Button loading={save.isPending} disabled={version.isLoading} onClick={onSave}>
                {t('appVersion.save')}
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
};
